// 클레임 보상기준 마스터(구성안 화면⑥) — 왼쪽 고객사 목록(claims/ClaimShipperPanel) · 머리(공문 표기 · 보기 전용 · 엑셀 업로드 · 변경이력 · 저장)
// · 고객사 배너 · 품온 토글 · 두 열(왼쪽 ① 유형별 변상 기준 / 오른쪽 ② 비용 단가 · 제품가 기준) · 아래 ③ 공통 규칙(규칙 문구 + 숫자 설정
// · 승인자 점검) · 맨 아래 ④ 택배사 기준(기본 접힘) + 변경이력 패널.
// 서버 routers/claims.py · 응답 모양은 services/claim_master.load_standards. 고객사를 고르면 standards({shipper_id}) 로 다시 읽는다
//   — 행 거르기(scopeKey)는 scope.has_overrides 면 그 고객사 키, 아니면 ''(공통). 공통 기준 화면의 동작은 그대로다.
// 🔴 설정(③)·택배사 기준(④)은 고객사와 무관하게 공통이다. 고객사 예외 행을 보일 때는 공통 단가표 엑셀 업로드·항목 추가를 숨긴다
//    (둘 다 공통 행에만 쓴다 — 보이지 않는 행을 바꾸지 않게).
// 🔴 ② 일반사고 제품가 기준만 범위를 따른다 — 공통 범위는 공통 설정 · 등록된 고객사는 그 고객사 값(공통 따름/매입가/판매가+비율 →
//    PUT /standards 의 shipper_standard) · 미등록 고객사는 공통 값을 글자로만. 고객사 범위에서는 공통 토글을 그리지 않는다.
// 🔴 GET 의 can_edit(= claim-admin) 가 거짓이면 입력 대신 글자를 보이고 저장·엑셀 업로드·추가·수정·사용 중지·동기화·확정·승인자 점검
//    단추는 그리지 않는다(변경이력·상세 보기는 있다).
// 🔴 ①·②·③ 의 표 안 입력은 초안(바뀐 칸만)으로 모았다가 머리 [저장] 한 번에 PUT /standards 로 보낸다 — 원래 값과 같아지면 초안에서 지운다
//    · 바뀐 칸이 없으면 요청하지 않는다 · 성공하면 GET 을 다시 읽는다 · 실패(422·404·409)는 '저장 대기' 줄(role=alert)과 toast 로 보이고
//    초안은 그대로 두고 해당 행을 강조한다. 초안이 있는 채로 편집기·추가·업로드·동기화를 누르면 confirmDialog 로 묻는다(계속하면 초안을 버린다).
// 🔴 상세·수정 편집기와 택배사 기준은 지금처럼 행 하나를 바로 저장한다(단건 PATCH). 삭제 단추는 없다(서버에 DELETE 경로가 없다 — 사용 중지만).
// 🔴 자동 검증(공통 규칙)·기한 경과 경고·재고조정 요청 자동 생성은 2단계 등록·승인 화면 몫이다 — 여기서는 값과 표시까지만.
// 🔴 저장 대기 초안이 있는 채로 다른 고객사(또는 공통 기준)를 고르면 confirmDialog 로 묻는다(계속하면 초안을 버린다).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconReportMoney } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Page, PageHeader, Button } from '../components/ui';
import { claimsAPI } from '../api/client';
import { confirmDialog, toast } from '../components/AppDialog';
import { fmtKstDateTime } from '../utils/kstTime';
import { blobDownload } from '../utils/meetingUtil';
import { COLORS, badge, card, input } from '../utils/uiStyles';
import ClaimHistoryPanel from './claims/ClaimHistoryPanel';
import ClaimShipperPanel, { SHIPPER_ZONES, ShipperStandardForm } from './claims/ClaimShipperPanel';
import {
  ACCIDENT_TABS, COMP_MARKS, COURIER_SYNC_STATUS, DEADLINE_BASES, FIELD_LABELS, GENERAL_RULE_GROUPS, SOURCE_LABELS, asText,
  changedFields, errorText, fmtPct, fmtWon, historyValue, labelOf, numOrNull, statusOf, textOrNull,
} from './claims/claimLabels';

const sectionCard = { ...card, marginTop: '16px' };
const sectionTitle = { margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text };
const sectionHead = { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' };
const guide = { fontSize: '12px', color: COLORS.textSub, margin: '0 0 10px' };
const subTitle = { margin: '18px 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.text };
const th = { padding: '6px 8px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub, minWidth: '140px' };
const checkBox = { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.text };
const formGrid = { display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' };
const editorBox = { marginTop: '12px', padding: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', background: COLORS.surface };
const errText = { color: COLORS.dangerText, fontSize: '12px', marginTop: '8px' };
const rowActions = { display: 'flex', gap: '6px', flexWrap: 'wrap' };
const errRow = { background: COLORS.dangerSoft };
const changedInput = { borderColor: COLORS.primary, background: COLORS.primarySoft };
const smallNote = { fontSize: '11px', color: COLORS.textSub, marginTop: '2px' };
const NO_CHANGE = '바뀐 칸이 없습니다.';
const EXPORT_FALLBACK = '클레임_비용단가.xlsx';
const SALE_RATIO_MISSING = '판매가 기준이면 비율 협의값(%)을 넣어야 합니다.';

// 행 거르기는 여기 한 곳 — scopeKey '' = 공통 · 'cs:<id>' = 그 고객사 예외 행(화면이 scope 로 정한다).
const inScopeOf = (scopeKey) => (row) => (row.shipper_key || '') === scopeKey;
const sortRows = (rows) => [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0));
const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

// 품온 토글 — 구성안 순서 [❄ 냉장/냉동] [☀ 상온] · 기본은 상온.
const ZONES = [
  { key: 'cold', icon: '❄', label: '냉장/냉동' },
  { key: 'ambient', icon: '☀', label: '상온' },
];
const COMP_COLUMNS = ['귀책', '유형', '처리', '물류비', '제품가', '재고조정', '접수기한'];
const CATEGORY_LABEL = Object.fromEntries(ACCIDENT_TABS.map((t) => [t.key, t.label]));   // 일반사고 · 택배사고
const CATEGORY_TONE = { general: 'blue', courier: 'orange' };
// 공통 규칙 문구(③ ✓ 목록)·제품가 기준(②) 에 따로 보이는 설정 — 나머지는 ③ 숫자 설정.
const RULE_TEXTS_KEY = 'rule_texts';
const PRICE_KEYS = ['product_price_basis', 'product_sale_ratio_pct'];
// 화면에서만 숨기는 옛 글자 설정 — 일반사고 제품가는 ② 토글이, 택배사고 제품가는 ③ 규칙 문구가 대신한다(DB 행·서버 검사는 그대로).
const HIDDEN_SETTING_KEYS = ['product_basis_general', 'product_basis_courier'];
const PRICE_GUIDE = '일반사고 제품가에만 씁니다 · 판매가면 협의 비율을 곱합니다 · 택배사고 제품가는 ③ 공통 규칙(결제금액에서 부가세 공제)을 따릅니다';
const SHIPPER_PRICE_NOTE = '고객사 기준을 등록하면 이 고객사만의 제품가 기준을 정할 수 있습니다';
// 고객사 제품가 기준의 원래 값(입력 글자) — 기준 ''(= null) 이면 공통 기준을 따른다.
const shipperPriceOrig = (std) => ({
  product_price_basis: (std && std.product_price_basis) || '',
  product_sale_ratio_pct: asText(std && std.product_sale_ratio_pct),
});
// 제품가 기준 글자 — '매입가' · '판매가 90%'(비율이 비면 '판매가 —%').
const basisText = (basis, ratio) => (basis === 'sale' ? `판매가 ${asText(ratio).trim() || '—'}%` : '매입가');

// 표 안 입력 → 서버 값(초안은 입력 글자 그대로 들고 있다가 비교·저장 때 바꾼다).
const COMP_CONVERT = {
  logistics_comp: (v) => v || null,
  product_comp: (v) => v || null,
  stock_adjust: (v) => String(v ?? '').trim(),
  shipper_deadline_days: (v) => numOrNull(v),
};
const compOriginal = (r, key) => (key === 'stock_adjust' ? (r.stock_adjust || '') : (r[key] ?? null));

// 초안 한 칸 — 원래 값과 같아지면 지운다(행에 남은 칸이 없으면 행도 지운다).
function putDraft(drafts, id, key, value, same) {
  const row = { ...(drafts[id] || {}) };
  if (same) delete row[key]; else row[key] = value;
  const next = { ...drafts };
  if (Object.keys(row).length) next[id] = row; else delete next[id];
  return next;
}
const countDraft = (drafts) => Object.values(drafts).reduce((n, row) => n + Object.keys(row).length, 0);
const mapRow = (row, convert) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, convert(k, v)]));

// 오류 칸 이름 → 강조할 행('comp_rules:1' · 'cost_items:11' · 'settings:vat_pct' · 'shipper_standard').
function errorTarget(field) {
  const row = /^(comp_rules|cost_items)\.(\d+)/.exec(field || '');
  if (row) return `${row[1]}:${row[2]}`;
  if (/^shipper_standard\./.test(field || '')) return 'shipper_standard';
  const setting = /^settings\.(\w+)/.exec(field || '');
  return setting ? `settings:${setting[1]}` : '';
}
function fieldOf(error) {
  const detail = error && error.response && error.response.data ? error.response.data.detail : null;
  return detail && typeof detail === 'object' && typeof detail.field === 'string' ? detail.field : '';
}
function fileNameOf(response, fallback) {
  const disposition = (response && response.headers && response.headers['content-disposition']) || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (!encoded) return fallback;
  try { return decodeURIComponent(encoded[1].trim()); } catch { return fallback; }
}

function NeedsCheck({ title }) {
  return <span data-testid="claim-needs-check" title={title} style={badge('warn')}>확인 필요</span>;
}
function Inactive({ title }) {
  return <span title={title} style={badge('gray')}>사용 중지</span>;
}
// tag = 제목 옆 배지 · bodyId 가 있으면 본문을 그 id 의 div 로 감싼다(접기 단추의 aria-controls 대상).
function Section({ id, title, tag, action, bodyId, children }) {
  return (
    <section aria-labelledby={id} style={sectionCard}>
      <div style={sectionHead}>
        <h2 id={id} style={sectionTitle}>{title}</h2>
        {tag}
        {action && <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>{action}</div>}
      </div>
      {bodyId ? <div id={bodyId}>{children}</div> : children}
    </section>
  );
}
function Field({ label, children }) {
  return <label style={fieldBox}><span>{label}</span>{children}</label>;
}
function ReasonInput({ value, onChange, disabled }) {
  return (
    <Field label="변경 사유(선택)">
      <input type="text" value={value} disabled={disabled} maxLength={200} onChange={(e) => onChange(e.target.value)} style={{ ...input, width: '260px' }} />
    </Field>
  );
}
// 사유가 있으면 본문에 싣는다(바뀐 칸이 없으면 서버가 사유를 버린다 — 여기서는 아예 보내지 않는다).
const withReason = (body, reason) => {
  const r = textOrNull(reason);
  return r ? { ...body, reason: r } : body;
};

// ── 품온 토글 ───────────────────────────────────────────────────────────────
function ZoneToggle({ zone, onChange }) {
  return (
    <div role="radiogroup" aria-label="품온" style={{ display: 'inline-flex', gap: '4px', padding: '3px', borderRadius: '8px', background: COLORS.primarySoft }}>
      {ZONES.map((z) => {
        const on = zone === z.key;
        return (
          <button key={z.key} type="button" role="radio" aria-checked={on} onClick={() => onChange(z.key)}
            style={{ border: 0, borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: on ? 600 : 400, background: on ? COLORS.surface : 'transparent', color: on ? COLORS.primaryDark : COLORS.textSub }}>
            <span aria-hidden="true">{z.icon} </span>{z.label}
          </button>
        );
      })}
    </div>
  );
}

// ── ① 유형별 변상 기준 ──────────────────────────────────────────────────────
const compBase = (r) => ({
  logistics_comp: r.logistics_comp ?? null,
  product_comp: r.product_comp ?? null,
  stock_adjust: r.stock_adjust || '',
  shipper_deadline_days: r.shipper_deadline_days ?? null,
  available: !!r.available,
  note: r.note ?? null,
  needs_check: !!r.needs_check,
});

function CompRuleEditor({ rule, labels, canEdit, runSave, onClose }) {
  const [draft, setDraft] = useState(() => ({
    logistics_comp: asText(rule.logistics_comp), product_comp: asText(rule.product_comp),
    stock_adjust: asText(rule.stock_adjust), shipper_deadline_days: asText(rule.shipper_deadline_days),
    available: !!rule.available, note: asText(rule.note), needs_check: !!rule.needs_check, reason: '',
  }));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const title = `${labelOf('rule_group', rule.rule_group, labels)} · ${labelOf('extent', rule.extent, labels)} · `
    + `${labelOf('handling', rule.handling, labels)} · ${labelOf('temp_zone', rule.temp_zone, labels)}`;

  const onSave = async () => {
    const body = changedFields(compBase(rule), {
      logistics_comp: draft.logistics_comp || null,
      product_comp: draft.product_comp || null,
      stock_adjust: draft.stock_adjust.trim(),
      shipper_deadline_days: numOrNull(draft.shipper_deadline_days),
      available: draft.available,
      note: textOrNull(draft.note),
      needs_check: draft.needs_check,
    });
    if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
    setBusy(true);
    setError('');
    const msg = await runSave(() => claimsAPI.patchCompRule(rule.id, withReason(body, draft.reason)));
    setBusy(false);
    if (msg) setError(msg); else onClose();
  };

  const markSelect = (key, label) => (
    <Field label={label}>
      <select value={draft[key]} disabled={!canEdit} onChange={(e) => set(key)(e.target.value)} style={{ ...input, width: '200px' }}>
        <option value="">—(없음)</option>
        {COMP_MARKS.map((m) => <option key={m} value={m}>{m} {labelOf('comp_mark', m, labels)}</option>)}
      </select>
    </Field>
  );

  return (
    <div data-testid="claim-comp-editor" style={editorBox}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: COLORS.text }}>{title}</div>
      <div style={formGrid}>
        {markSelect('logistics_comp', '물류비')}
        {markSelect('product_comp', '제품가')}
        <Field label="재고조정">
          <input type="text" value={draft.stock_adjust} disabled={!canEdit} onChange={(e) => set('stock_adjust')(e.target.value)} style={{ ...input, width: '160px' }} />
        </Field>
        <Field label="고객사 접수기한(출고일+N일)">
          <input type="text" inputMode="numeric" value={draft.shipper_deadline_days} disabled={!canEdit} onChange={(e) => set('shipper_deadline_days')(e.target.value)} style={{ ...input, width: '120px' }} />
        </Field>
        <label style={checkBox}>
          <input type="checkbox" checked={draft.available} disabled={!canEdit} onChange={(e) => set('available')(e.target.checked)} />처리 가능
        </label>
        <Field label="메모">
          <input type="text" value={draft.note} disabled={!canEdit} onChange={(e) => set('note')(e.target.value)} style={{ ...input, width: '260px' }} />
        </Field>
        <label style={checkBox}>
          <input type="checkbox" checked={draft.needs_check} disabled={!canEdit} onChange={(e) => set('needs_check')(e.target.checked)} />확인 필요로 표시
        </label>
        {canEdit && <ReasonInput value={draft.reason} onChange={set('reason')} />}
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ ...rowActions, marginTop: '10px' }}>
        {canEdit && <Button onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>}
        <Button variant="secondary" onClick={onClose}>닫기</Button>
      </div>
    </div>
  );
}

const categoryOf = (r) => (GENERAL_RULE_GROUPS.includes(r.rule_group) ? 'general' : 'courier');
const typeKey = (r) => `${r.rule_group}/${r.extent}`;
// 유형 — 일반사고는 '{묶음}({범위})', 택배사고는 묶음.
const typeLabel = (r, labels) => (categoryOf(r) === 'general'
  ? `${labelOf('rule_group', r.rule_group, labels)}(${labelOf('extent', r.extent, labels)})`
  : labelOf('rule_group', r.rule_group, labels));
// 이어지는 같은 값의 행 수(rowSpan) — 첫 행에만 수가 있고 나머지는 0.
function runSpans(rows, keyOf) {
  const out = rows.map(() => 0);
  let start = 0;
  rows.forEach((r, i) => {
    if (i > 0 && keyOf(r) === keyOf(rows[start])) { out[start] += 1; return; }
    start = i;
    out[i] = 1;
  });
  return out;
}

function CompSection({ rules, zone, scopeKey, labels, canEdit, runSave, draft, onDraft, errorKey, guard }) {
  const [openId, setOpenId] = useState(null);
  const rows = useMemo(() => {
    const inScope = inScopeOf(scopeKey);
    return sortRows(rules.filter((r) => inScope(r) && r.temp_zone === zone));
  }, [rules, zone, scopeKey]);
  const spans = useMemo(() => ({ category: runSpans(rows, categoryOf), type: runSpans(rows, typeKey) }), [rows]);
  const open = rows.find((r) => r.id === openId) || null;   // 품온을 바꾸면 편집기도 닫힌다
  const markLabel = (m) => labelOf('comp_mark', m, labels);
  const legend = COMP_MARKS.map((m) => `${m} ${markLabel(m)}`).join(' · ');

  const onDetail = async (r) => {
    if (openId === r.id) { setOpenId(null); return; }
    if (await guard()) setOpenId(r.id);
  };
  const markCell = (r, d, key, name, label) => {
    if (!canEdit) return r[key] ? <span title={markLabel(r[key])}>{r[key]}</span> : '—';
    return (
      <select aria-label={`${name} ${label}`} value={has(d, key) ? d[key] : asText(r[key])} onChange={(e) => onDraft(r, key, e.target.value)}
        style={{ ...input, width: '64px', ...(has(d, key) ? changedInput : {}) }}>
        {COMP_MARKS.map((m) => <option key={m} value={m} title={markLabel(m)}>{m}</option>)}
        <option value="">—</option>
      </select>
    );
  };

  return (
    <Section id="claim-sec-comp" title="① 유형별 변상 기준">
      <p style={guide}>{labelOf('temp_zone', zone, labels)} · 재출고/환불별 변상 여부 · [상세] 로 처리 가능 여부·메모를 {canEdit ? '고칠' : '볼'} 수 있습니다.</p>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="claim-comp-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
          <thead>
            <tr>{COMP_COLUMNS.map((c) => <th key={c} style={th}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={COMP_COLUMNS.length} style={{ ...td, color: COLORS.textMuted }}>이 품온의 변상 기준이 없습니다.</td></tr>
            )}
            {rows.map((r, i) => {
              const handling = labelOf('handling', r.handling, labels);
              const name = `${typeLabel(r, labels)} ${handling}`;
              const d = draft[r.id] || {};
              const err = errorKey === `comp_rules:${r.id}`;
              const group = spans.type[i] ? rows.slice(i, i + spans.type[i]) : [];
              const category = categoryOf(r);
              const detail = (
                <Button variant="quiet" size="sm" aria-label={`${name} 상세`} aria-pressed={openId === r.id} onClick={() => onDetail(r)}>상세</Button>
              );
              return (
                <tr key={r.id} data-testid="claim-comp-row" data-error={err ? 'true' : undefined} style={err ? errRow : undefined}>
                  {spans.category[i] > 0 && (
                    <td rowSpan={spans.category[i]} style={td}><span style={badge(CATEGORY_TONE[category])}>{CATEGORY_LABEL[category]}</span></td>
                  )}
                  {spans.type[i] > 0 && (
                    <td rowSpan={spans.type[i]} style={td}>
                      <div>{typeLabel(r, labels)}</div>
                      {group.some((x) => x.needs_check || !x.active) && (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {group.filter((x) => x.needs_check).map((x) => <NeedsCheck key={`n${x.id}`} title={labelOf('handling', x.handling, labels)} />)}
                          {group.filter((x) => !x.active).map((x) => <Inactive key={`i${x.id}`} title={labelOf('handling', x.handling, labels)} />)}
                        </div>
                      )}
                    </td>
                  )}
                  {r.available ? (
                    <>
                      <td style={td}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>{handling}{detail}</span></td>
                      <td style={td}>{markCell(r, d, 'logistics_comp', name, '물류비')}</td>
                      <td style={td}>{markCell(r, d, 'product_comp', name, '제품가')}</td>
                      <td style={td}>
                        {canEdit ? (
                          <input type="text" aria-label={`${name} 재고조정`} placeholder="불필요" maxLength={30}
                            value={has(d, 'stock_adjust') ? d.stock_adjust : asText(r.stock_adjust)} onChange={(e) => onDraft(r, 'stock_adjust', e.target.value)}
                            style={{ ...input, width: '120px', ...(has(d, 'stock_adjust') ? changedInput : {}) }} />
                        ) : (r.stock_adjust || '불필요')}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {canEdit ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            출고 +
                            <input type="text" inputMode="numeric" aria-label={`${name} 접수기한(일)`}
                              value={has(d, 'shipper_deadline_days') ? d.shipper_deadline_days : asText(r.shipper_deadline_days)}
                              onChange={(e) => onDraft(r, 'shipper_deadline_days', e.target.value)}
                              style={{ ...input, width: '56px', ...(has(d, 'shipper_deadline_days') ? changedInput : {}) }} />
                            일
                          </span>
                        ) : (r.shipper_deadline_days == null ? '—' : `출고 +${r.shipper_deadline_days}일`)}
                      </td>
                    </>
                  ) : (
                    <td colSpan={5} style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ color: COLORS.dangerText, fontWeight: 500 }}>{r.note || '처리 불가'}</span>{detail}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div data-testid="claim-comp-legend" style={{ ...guide, margin: '10px 0 0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span>{legend}</span>
        <span>상세사유 ↔ 유형 연결은 <a href="/claims/reasons" style={{ color: COLORS.primaryDark }}>사유코드 관리</a>에서 설정</span>
      </div>
      {open && (
        <CompRuleEditor key={open.id} rule={open} labels={labels} canEdit={canEdit} runSave={runSave} onClose={() => setOpenId(null)} />
      )}
    </Section>
  );
}

// ── ② 비용 단가 · 제품가 기준 ───────────────────────────────────────────────
const COST_FIELDS = ['name', 'group_name', 'basis', 'unit_price', 'unit_price_max', 'note', 'needs_check'];
const costBase = (it) => Object.fromEntries(COST_FIELDS.map((k) => [k, k === 'needs_check' ? !!it[k] : (it[k] ?? null)]));
const costItemName = (it) => it.group_name || it.name;
// 단가 표기 — 상한이 있으면 '2,650~3,400원'.
function fmtPrice(it) {
  if (it.unit_price_max == null) return fmtWon(it.unit_price);
  const low = Number(it.unit_price);
  return `${Number.isFinite(low) ? low.toLocaleString('ko-KR') : asText(it.unit_price)}~${fmtWon(it.unit_price_max)}`;
}

function CostEditor({ item, runSave, onClose }) {
  const creating = !item;
  const [draft, setDraft] = useState(() => ({
    code: '', ...Object.fromEntries(COST_FIELDS.map((k) => [k, k === 'needs_check' ? !!(item && item[k]) : asText(item && item[k])])), reason: '',
  }));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const onSave = async () => {
    const next = {
      name: draft.name.trim(),
      group_name: textOrNull(draft.group_name),
      basis: textOrNull(draft.basis),
      unit_price: numOrNull(draft.unit_price),
      unit_price_max: numOrNull(draft.unit_price_max),
      note: textOrNull(draft.note),
      needs_check: draft.needs_check,
    };
    let call;
    if (creating) {
      const body = { code: draft.code.trim(), name: next.name, unit_price: next.unit_price, needs_check: next.needs_check };
      ['group_name', 'basis', 'unit_price_max', 'note'].forEach((k) => { if (next[k] !== null) body[k] = next[k]; });
      call = () => claimsAPI.createCostItem(withReason(body, draft.reason));
    } else {
      const body = changedFields(costBase(item), next);
      if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
      call = () => claimsAPI.patchCostItem(item.id, withReason(body, draft.reason));
    }
    setBusy(true);
    setError('');
    const msg = await runSave(call, creating ? '추가했습니다' : '저장했습니다');
    setBusy(false);
    if (msg) setError(msg); else onClose();
  };

  const text = (key, label, width) => (
    <Field label={label}>
      <input type="text" value={draft[key]} onChange={(e) => set(key)(e.target.value)} style={{ ...input, width }} />
    </Field>
  );
  const num = (key, label) => (
    <Field label={label}>
      <input type="text" inputMode="numeric" value={draft[key]} onChange={(e) => set(key)(e.target.value)} style={{ ...input, width: '120px' }} />
    </Field>
  );

  return (
    <div data-testid="claim-cost-editor" style={editorBox}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: COLORS.text }}>{creating ? '비용 단가 추가' : `${item.name} 수정`}</div>
      <div style={formGrid}>
        {creating && text('code', '단가 코드(영문 소문자·숫자·_)', '180px')}
        {text('name', '단가 이름', '180px')}
        {text('group_name', '항목', '140px')}
        {text('basis', '기준', '140px')}
        {num('unit_price', '단가(원)')}
        {num('unit_price_max', '최대 단가(원)')}
        {text('note', '비고', '240px')}
        <label style={checkBox}>
          <input type="checkbox" checked={draft.needs_check} onChange={(e) => set('needs_check')(e.target.checked)} />확인 필요로 표시
        </label>
        <ReasonInput value={draft.reason} onChange={set('reason')} />
      </div>
      <p style={{ ...guide, margin: '8px 0 0' }}>최대 단가를 넣으면 범위 단가(단가~최대 단가)로 보입니다 · 비우면 단일 단가.</p>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ ...rowActions, marginTop: '10px' }}>
        <Button onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>
        <Button variant="secondary" onClick={onClose}>닫기</Button>
      </div>
    </div>
  );
}

// 일반사고 제품가 기준 — (a) 공통 범위(scope null) = 공통 설정 토글(settingDraft)
// · (b) 등록된 고객사 = [공통 기준 따름(현재 …)] [매입가] [판매가] + 판매가면 비율(shipperDraft · '' = 공통 따름)
// · (c) 미등록 고객사 = 공통 값 글자 + 등록 안내. canEdit 이 거짓이면 모두 글자로만.
function PriceBasis({ basisSetting, ratioSetting, canEdit, valueOf, onSetting, errorKey, scope, shipperDraft, onShipper }) {
  const commonBasis = valueOf(basisSetting.key);
  const commonRatio = ratioSetting ? valueOf(ratioSetting.key) : '';
  const plain = (text) => <span style={{ fontSize: '13px', color: COLORS.text }}>{text}</span>;
  const pick = (on, label, onClick) => (
    <Button variant={on ? 'primary' : 'secondary'} size="sm" aria-pressed={on} onClick={onClick}>{label}</Button>
  );
  const group = (children) => (
    <span role="group" aria-label="일반사고 제품가 기준" style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>{children}</span>
  );
  const ratioInput = (value, onChange, changed) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.text }}>
      비율 협의
      <input type="text" inputMode="numeric" aria-label="판매가 비율 협의(%)" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ ...input, width: '64px', ...(changed ? changedInput : {}) }} />
      %
    </span>
  );

  let err = false;
  let content;
  let note = '';
  if (!scope) {
    const isSale = commonBasis === 'sale';
    err = errorKey === `settings:${basisSetting.key}` || (!!ratioSetting && errorKey === `settings:${ratioSetting.key}`);
    content = (
      <>
        {canEdit ? group(
          <>
            {pick(commonBasis === 'purchase', '매입가', () => onSetting(basisSetting, 'purchase'))}
            {pick(isSale, '판매가', () => onSetting(basisSetting, 'sale'))}
          </>,
        ) : plain(isSale ? '판매가' : '매입가')}
        {isSale && ratioSetting && (canEdit
          ? ratioInput(commonRatio, (v) => onSetting(ratioSetting, v), commonRatio !== asText(ratioSetting.value))
          : plain(`비율 협의 ${commonRatio || '—'}%`))}
        {basisSetting.needs_check && <NeedsCheck title={basisSetting.label} />}
        {ratioSetting && ratioSetting.needs_check && <NeedsCheck title={ratioSetting.label} />}
      </>
    );
  } else if (scope.registered && scope.standard) {
    const orig = shipperPriceOrig(scope.standard);
    const value = (key) => (has(shipperDraft, key) ? shipperDraft[key] : orig[key]);
    const basis = value('product_price_basis');
    const ratio = value('product_sale_ratio_pct');
    const follow = `공통 기준 따름(현재 ${basisText(commonBasis, commonRatio)})`;
    err = errorKey === 'shipper_standard';
    content = canEdit ? (
      <>
        {group(
          <>
            {pick(basis === '', follow, () => onShipper({ product_price_basis: '', product_sale_ratio_pct: '' }))}
            {pick(basis === 'purchase', '매입가', () => onShipper({ product_price_basis: 'purchase' }))}
            {pick(basis === 'sale', '판매가', () => onShipper({ product_price_basis: 'sale' }))}
          </>,
        )}
        {basis === 'sale' && ratioInput(ratio, (v) => onShipper({ product_sale_ratio_pct: v }), has(shipperDraft, 'product_sale_ratio_pct'))}
      </>
    ) : plain(basis ? basisText(basis, ratio) : follow);
  } else {
    content = plain(`공통 기준 · ${basisText(commonBasis, commonRatio)}`);
    note = SHIPPER_PRICE_NOTE;
  }
  return (
    <div data-testid="claim-price-basis" data-error={err ? 'true' : undefined}
      style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${COLORS.borderSoft}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', ...(err ? errRow : {}) }}>
      <span style={{ fontSize: '13px', fontWeight: 600, color: COLORS.text }}>일반사고 제품가 기준</span>
      {content}
      <span style={{ ...guide, margin: 0, flexBasis: '100%' }}>{PRICE_GUIDE}</span>
      {note && <span style={{ ...guide, margin: 0, flexBasis: '100%' }}>{note}</span>}
    </div>
  );
}

function CostSection({ items, canEdit, canAdd = true, runSave, draft, onDraft, errorKey, guard, basisSetting, ratioSetting, valueOf, onSetting,
  scope, shipperDraft, onShipper }) {
  const [editing, setEditing] = useState(null);   // null | 'new' | 항목 id
  const current = typeof editing === 'number' ? items.find((it) => it.id === editing) : null;
  const spans = useMemo(() => runSpans(items, costItemName), [items]);
  const toggleActive = (it) => runSave(
    () => claimsAPI.patchCostItem(it.id, { active: !it.active }),
    it.active ? '사용 중지했습니다' : '다시 사용합니다',
  );
  const openEditor = async (target) => { if (await guard()) setEditing(target); };
  const priceInput = (it, d, key, label) => (
    <input type="text" inputMode="numeric" aria-label={`${it.name} ${label}`} value={has(d, key) ? d[key] : asText(it[key])}
      onChange={(e) => onDraft(it, key, e.target.value)} style={{ ...input, width: '84px', ...(has(d, key) ? changedInput : {}) }} />
  );

  return (
    <Section id="claim-sec-cost" title="② 비용 단가 · 제품가 기준"
      action={canEdit && canAdd && <Button variant="secondary" size="sm" onClick={() => openEditor('new')}>항목 추가</Button>}>
      <p style={guide}>WMS 출고정보에 박스 단가·작업비가 있으면 그 값이 우선합니다.</p>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="claim-cost-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: canEdit ? '460px' : '320px' }}>
          <thead>
            <tr>
              <th style={th}>항목</th><th style={th}>기준</th><th style={th}>단가</th>
              {canEdit && <th style={th}>동작</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={canEdit ? 4 : 3} style={{ ...td, color: COLORS.textMuted }}>비용 단가가 없습니다.</td></tr>
            )}
            {items.map((it, i) => {
              const d = draft[it.id] || {};
              const err = errorKey === `cost_items:${it.id}`;
              const hasMax = it.unit_price_max != null || has(d, 'unit_price_max');
              return (
                <tr key={it.id} data-testid="claim-cost-row" data-error={err ? 'true' : undefined} style={err ? errRow : undefined}>
                  {spans[i] > 0 && <td rowSpan={spans[i]} style={td}>{costItemName(it)}</td>}
                  <td style={td}>
                    <span style={{ color: COLORS.textSub }}>{it.basis || (it.group_name && it.group_name !== it.name ? it.name : '—')}</span>
                    {(it.needs_check || !it.active) && (
                      <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                        {it.needs_check && <NeedsCheck />}
                        {!it.active && <Inactive />}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {canEdit ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {priceInput(it, d, 'unit_price', '단가')}
                        {hasMax && <>~{priceInput(it, d, 'unit_price_max', '최대 단가')}</>}
                        원
                      </span>
                    ) : fmtPrice(it)}
                    {it.note && <div style={{ ...smallNote, whiteSpace: 'normal' }}>{it.note}</div>}
                  </td>
                  {canEdit && (
                    <td style={td}>
                      <span style={rowActions}>
                        <Button variant="quiet" size="sm" aria-label={`${it.name} 수정`} onClick={() => openEditor(it.id)}>수정</Button>
                        <Button variant="quiet" size="sm" aria-label={`${it.name} ${it.active ? '사용 중지' : '다시 사용'}`} onClick={() => toggleActive(it)}>
                          {it.active ? '사용 중지' : '다시 사용'}
                        </Button>
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && canAdd && editing === 'new' && <CostEditor key="new" item={null} runSave={runSave} onClose={() => setEditing(null)} />}
      {canEdit && current && <CostEditor key={current.id} item={current} runSave={runSave} onClose={() => setEditing(null)} />}
      {basisSetting && (
        <PriceBasis basisSetting={basisSetting} ratioSetting={ratioSetting} canEdit={canEdit} valueOf={valueOf} onSetting={onSetting} errorKey={errorKey}
          scope={scope} shipperDraft={shipperDraft} onShipper={onShipper} />
      )}
    </Section>
  );
}

// ── ③ 공통 규칙 — 규칙 문구(✓ 목록) · 숫자 설정 · 승인자 점검 ────────────────────
function ApproverResult({ result }) {
  const s = result.summary || {};
  const centers = Array.isArray(result.centers) ? result.centers : [];
  return (
    <div data-testid="claim-approver-result" style={{ marginBottom: '12px' }}>
      <p style={guide}>
        직책 키워드 «{result.keyword}» · 활성 센터 {s.active_centers ?? centers.length}곳 · 센터장 없는 센터 {s.without_head ?? 0}곳
        {' '}· 계정 미연결 센터장 {s.head_without_account ?? 0}명
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
          <thead>
            <tr><th style={th}>센터</th><th style={th}>센터장 수</th><th style={th}>계정 연결 수</th><th style={th}>경고</th></tr>
          </thead>
          <tbody>
            {centers.map((c) => {
              const warns = [];
              if (!c.org_set) warns.push('조직 미설정');
              if (!c.depot_set) warns.push('센터코드 미설정');
              if (!c.heads) warns.push('센터장 없음');
              else if (c.heads_with_account < c.heads) warns.push('계정 미연결 센터장 있음');
              return (
                <tr key={c.center_key} data-testid="claim-approver-row">
                  <td style={td}>{c.display_name || c.center_key}</td>
                  <td style={td}>{c.heads}</td>
                  <td style={td}>{c.heads_with_account}</td>
                  <td style={{ ...td, color: warns.length ? COLORS.dangerText : COLORS.textMuted }}>{warns.length ? warns.join(' · ') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 숫자 설정 행 — 승인 기준·증빙·택배사 알림 등(옛 ④ 운영 설정). 입력 형식은 value_type 을 따른다.
function SettingRows({ settings, canEdit, valueOf, onSetting, errorKey }) {
  const inputFor = (s) => {
    const changed = valueOf(s.key) !== asText(s.value);
    const common = {
      'aria-label': s.label || s.key,
      value: valueOf(s.key),
      onChange: (e) => onSetting(s, e.target.value),
    };
    const mark = changed ? changedInput : {};
    if (s.value_type === 'longtext') return <textarea {...common} rows={5} style={{ ...input, width: '100%', resize: 'vertical', ...mark }} />;
    if (s.value_type === 'int') return <input type="text" inputMode="numeric" {...common} style={{ ...input, width: '160px', ...mark }} />;
    if (s.value_type === 'days_list') return <input type="text" placeholder="예: 3,1" {...common} style={{ ...input, width: '160px', ...mark }} />;
    return <input type="text" {...common} style={{ ...input, width: s.value_type === 'ident' ? '220px' : '100%', maxWidth: '420px', ...mark }} />;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {settings.map((s) => {
        const err = errorKey === `settings:${s.key}`;
        return (
          <div key={s.key} data-testid="claim-setting-row" data-error={err ? 'true' : undefined}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start', ...(err ? errRow : {}) }}>
            <div style={{ width: '260px', flexShrink: 0, fontSize: '13px', color: COLORS.text, paddingTop: '7px' }}>
              {s.label || s.key}
              {s.needs_check && <span style={{ marginLeft: '6px' }}><NeedsCheck /></span>}
            </div>
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', gap: '6px', paddingTop: canEdit ? 0 : '7px' }}>
              {canEdit ? inputFor(s) : (
                <span style={{ fontSize: '13px', color: COLORS.text, whiteSpace: 'pre-wrap' }}>{asText(s.value) || '—'}</span>
              )}
              {s.unit && <span style={{ fontSize: '12px', color: COLORS.textSub }}>{s.unit}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RulesSection({ setting, settings, canEdit, valueOf, onSetting, errorKey }) {
  const [editing, setEditing] = useState(false);
  const [approver, setApprover] = useState(null);
  const [approverBusy, setApproverBusy] = useState(false);
  const [approverError, setApproverError] = useState('');
  const text = setting ? valueOf(setting.key) : '';
  const lines = text.split('\n').map((s) => s.trim()).filter(Boolean);
  const err = setting && errorKey === `settings:${setting.key}`;

  const onApprover = async () => {
    setApproverBusy(true);
    setApproverError('');
    try {
      const r = await claimsAPI.approverCheck();
      setApprover(r.data || null);
    } catch (e) {
      const msg = errorText(e, '승인자 점검에 실패했습니다');
      setApproverError(msg);
      toast(msg, 'error');
    } finally {
      setApproverBusy(false);
    }
  };

  return (
    <Section id="claim-sec-rules" title="③ 공통 규칙"
      action={canEdit && (
        <>
          {setting && <Button variant="secondary" size="sm" aria-pressed={editing} onClick={() => setEditing((v) => !v)}>편집</Button>}
          <Button variant="secondary" size="sm" onClick={onApprover} disabled={approverBusy}>{approverBusy ? '점검 중…' : '승인자 점검'}</Button>
        </>
      )}>
      {approverError && <div role="alert" style={{ ...errText, marginTop: 0, marginBottom: '8px' }}>{approverError}</div>}
      {approver && <ApproverResult result={approver} />}
      {!setting && <p style={guide}>공통 규칙 문구 설정이 아직 없습니다.</p>}
      {setting && (
        <div data-error={err ? 'true' : undefined} style={err ? errRow : undefined}>
          {setting.needs_check && <div style={{ marginBottom: '6px' }}><NeedsCheck title={setting.label} /></div>}
          <ul aria-label="공통 규칙 목록" data-testid="claim-rule-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {lines.length === 0 && <li style={{ fontSize: '13px', color: COLORS.textMuted }}>등록된 규칙이 없습니다.</li>}
            {lines.map((line, i) => (
              <li key={`${i}:${line}`} style={{ display: 'flex', gap: '6px', fontSize: '13px', color: COLORS.text }}>
                <span aria-hidden="true" style={{ color: COLORS.primary, fontWeight: 600 }}>✓</span>{line}
              </li>
            ))}
          </ul>
          {canEdit && editing && (
            <textarea aria-label={setting.label || '공통 규칙 문구'} rows={8} value={text} onChange={(e) => onSetting(setting, e.target.value)}
              style={{ ...input, width: '100%', marginTop: '10px', resize: 'vertical', ...(text !== asText(setting.value) ? changedInput : {}) }} />
          )}
          <p style={{ ...guide, margin: '8px 0 0' }}>한 줄에 규칙 하나 · 2단계 등록·승인 화면에서 자동 검증 규칙으로 씁니다.</p>
        </div>
      )}
      {settings.length > 0 && (
        <>
          <h3 style={subTitle}>숫자 설정</h3>
          <p style={guide}>승인 기준·증빙·택배사 알림 등 · 기준 공문 번호는 화면 머리에 보입니다.</p>
          <SettingRows settings={settings} canEdit={canEdit} valueOf={valueOf} onSetting={onSetting} errorKey={errorKey} />
        </>
      )}
    </Section>
  );
}

// ── ④ 택배사 기준 — 맨 아래 · 기본 접힘(미매핑이 있으면 접혀 있어도 머리에 배지) ─────────
const GROUP_FIELDS = [
  'name', 'comp_pct', 'box_limit_krw', 'deadline_basis', 'deadline_days', 'loss_basis', 'loss_start_days', 'loss_end_days',
  'not_applicable', 'match_keywords', 'note', 'needs_check',
];
const groupBase = (g) => Object.fromEntries(GROUP_FIELDS.map((k) => [k, (k === 'not_applicable' || k === 'needs_check') ? !!g[k] : (g[k] ?? null)]));

function GroupEditor({ group, labels, runSave, onClose }) {
  const creating = !group;
  const [draft, setDraft] = useState(() => ({
    code: '',
    ...Object.fromEntries(GROUP_FIELDS.map((k) => [k, (k === 'not_applicable' || k === 'needs_check') ? !!(group && group[k]) : asText(group && group[k])])),
    reason: '',
  }));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const onSave = async () => {
    const next = {
      name: draft.name.trim(),
      comp_pct: numOrNull(draft.comp_pct),
      box_limit_krw: numOrNull(draft.box_limit_krw),
      deadline_basis: draft.deadline_basis || null,
      deadline_days: numOrNull(draft.deadline_days),
      loss_basis: draft.loss_basis || null,
      loss_start_days: numOrNull(draft.loss_start_days),
      loss_end_days: numOrNull(draft.loss_end_days),
      not_applicable: draft.not_applicable,
      match_keywords: textOrNull(draft.match_keywords),
      note: textOrNull(draft.note),
      needs_check: draft.needs_check,
    };
    let call;
    if (creating) {
      const body = { code: draft.code.trim() };
      Object.entries(next).forEach(([k, v]) => { if (v !== null && v !== '') body[k] = v; });
      call = () => claimsAPI.createCourierGroup(withReason(body, draft.reason));
    } else {
      const body = changedFields(groupBase(group), next);
      if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
      call = () => claimsAPI.patchCourierGroup(group.code, withReason(body, draft.reason));
    }
    setBusy(true);
    setError('');
    const msg = await runSave(call, creating ? '추가했습니다' : '저장했습니다');
    setBusy(false);
    if (msg) setError(msg); else onClose();
  };

  const basisSelect = (key, label) => (
    <Field label={label}>
      <select value={draft[key]} onChange={(e) => set(key)(e.target.value)} style={{ ...input, width: '140px' }}>
        <option value="">—(없음)</option>
        {DEADLINE_BASES.map((b) => <option key={b} value={b}>{labelOf('deadline_basis', b, labels)}</option>)}
      </select>
    </Field>
  );
  const numField = (key, label, width = '110px') => (
    <Field label={label}>
      <input type="text" inputMode="numeric" value={draft[key]} onChange={(e) => set(key)(e.target.value)} style={{ ...input, width }} />
    </Field>
  );

  return (
    <div data-testid="claim-group-editor" style={editorBox}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: COLORS.text }}>{creating ? '택배사 그룹 추가' : `${group.name} 수정`}</div>
      <div style={formGrid}>
        {creating && (
          <Field label="그룹 코드(영문 대문자·숫자·_)">
            <input type="text" value={draft.code} onChange={(e) => set('code')(e.target.value)} style={{ ...input, width: '160px' }} />
          </Field>
        )}
        <Field label="그룹 이름">
          <input type="text" value={draft.name} onChange={(e) => set('name')(e.target.value)} style={{ ...input, width: '160px' }} />
        </Field>
        {numField('comp_pct', '보상률(%)')}
        {numField('box_limit_krw', 'BOX당 한도(원)', '130px')}
        {basisSelect('deadline_basis', '접수기한 기준')}
        {numField('deadline_days', '접수기한(일)')}
        {basisSelect('loss_basis', '분실 기준')}
        {numField('loss_start_days', '분실 시작(일)')}
        {numField('loss_end_days', '분실 끝(일)')}
        <Field label="자동추정 키워드(콤마 구분)">
          <input type="text" value={draft.match_keywords} onChange={(e) => set('match_keywords')(e.target.value)} style={{ ...input, width: '220px' }} />
        </Field>
        <Field label="비고">
          <input type="text" value={draft.note} onChange={(e) => set('note')(e.target.value)} style={{ ...input, width: '200px' }} />
        </Field>
        <label style={checkBox}>
          <input type="checkbox" checked={draft.not_applicable} onChange={(e) => set('not_applicable')(e.target.checked)} />해당없음(보상 대상 아님)
        </label>
        <label style={checkBox}>
          <input type="checkbox" checked={draft.needs_check} onChange={(e) => set('needs_check')(e.target.checked)} />확인 필요로 표시
        </label>
        <ReasonInput value={draft.reason} onChange={set('reason')} />
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ ...rowActions, marginTop: '10px' }}>
        <Button onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>
        <Button variant="secondary" onClick={onClose}>닫기</Button>
      </div>
    </div>
  );
}

function SyncResult({ result }) {
  const sources = Array.isArray(result.sources) ? result.sources : [];
  return (
    <div data-testid="claim-sync-result" style={{ ...guide, marginTop: '8px' }}>
      {sources.map((s) => {
        const src = SOURCE_LABELS[s.source] || s.source;
        let text;
        if (s.status === 'ok') text = `${src} ${COURIER_SYNC_STATUS.ok} — 조회 ${s.fetched ?? 0} · 추가 ${s.added ?? 0} · 갱신 ${s.updated ?? 0} · 없어짐 ${s.missing ?? 0}`;
        else if (s.status === 'unset') text = COURIER_SYNC_STATUS.unset;
        else text = `${src} ${COURIER_SYNC_STATUS.error} — ${s.message || s.code || ''}`;
        return <div key={s.source} style={{ color: s.status === 'ok' ? COLORS.textSub : COLORS.dangerText }}>{text}</div>;
      })}
    </div>
  );
}

function CourierSection({ groups, codes, unmapped, labels, canEdit, runSave, onSynced, guard }) {
  const [open, setOpen] = useState(false);        // 기본 접힘 — 접혀 있으면 본문(그룹 표·편집기·코드 매핑·동기화 결과)을 그리지 않는다
  const [editing, setEditing] = useState(null);   // null | 'new' | 그룹 코드
  const [picks, setPicks] = useState({});         // 'source:code' → 고른 그룹 코드
  const [codeError, setCodeError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const current = editing && editing !== 'new' ? groups.find((g) => g.code === editing) : null;
  const groupName = (code) => {
    const g = groups.find((x) => x.code === code);
    return g ? g.name : (code || '—');
  };
  const activeGroups = groups.filter((g) => g.active);
  const keyOf = (c) => `${c.source}:${c.code}`;
  const pickOf = (c) => (Object.prototype.hasOwnProperty.call(picks, keyOf(c)) ? picks[keyOf(c)] : (c.group_code || c.suggested_group || ''));
  const openEditor = async (target) => { if (await guard()) setEditing(target); };

  const onConfirm = async (c) => {
    const pick = pickOf(c);
    if (!pick) { setCodeError('확정할 그룹을 고르세요.'); return; }
    const body = {};
    if (pick !== c.group_code) body.group_code = pick;
    if (!c.confirmed) body.confirmed = true;
    if (!Object.keys(body).length) { setCodeError(NO_CHANGE); return; }
    setCodeError('');
    const msg = await runSave(() => claimsAPI.patchCourierCode(c.source, c.code, body), '확정했습니다');
    if (msg) setCodeError(msg);
    else setPicks((p) => { const n = { ...p }; delete n[keyOf(c)]; return n; });
  };

  const onSync = async () => {
    if (!(await guard())) return;
    setSyncing(true);
    setSyncError('');
    try {
      const r = await claimsAPI.syncCouriers();
      setSyncResult(r.data || null);
      toast('택배사 코드를 동기화했습니다');
      await onSynced();
    } catch (e) {
      const msg = errorText(e, '동기화에 실패했습니다');
      setSyncError(msg);
      toast(msg, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const tag = unmapped > 0 ? <span data-testid="claim-unmapped-badge" style={badge('danger')}>미매핑 {unmapped}건</span> : null;
  const toggle = (
    <Button variant="secondary" size="sm" aria-expanded={open} aria-controls="claim-sec-courier-body" onClick={() => setOpen((v) => !v)}>
      {open ? '접기' : '펼치기'}
    </Button>
  );
  if (!open) return <Section id="claim-sec-courier" title="④ 택배사 기준" tag={tag} action={toggle} bodyId="claim-sec-courier-body" />;

  return (
    <Section id="claim-sec-courier" title="④ 택배사 기준" tag={tag} bodyId="claim-sec-courier-body"
      action={(
        <>
          {canEdit && (
            <>
              <Button variant="secondary" size="sm" onClick={() => openEditor('new')}>그룹 추가</Button>
              <Button variant="secondary" size="sm" onClick={onSync} disabled={syncing}>{syncing ? '동기화 중…' : 'WMS 택배사 코드 동기화'}</Button>
            </>
          )}
          {toggle}
        </>
      )}>
      {syncError && <div role="alert" style={{ ...errText, marginTop: 0, marginBottom: '8px' }}>{syncError}</div>}
      {syncResult && <SyncResult result={syncResult} />}
      <h3 style={{ margin: '4px 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.text }}>택배사 그룹</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead>
            <tr>
              <th style={th}>그룹</th><th style={th}>보상률</th><th style={th}>BOX당 한도</th><th style={th}>접수기한</th>
              <th style={th}>분실 기간</th><th style={th}>자동추정 키워드</th><th style={th}>해당없음</th><th style={th}>상태</th>
              {canEdit && <th style={th}>동작</th>}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.code} data-testid="claim-group-row">
                <td style={td}>{g.name} <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{g.code}</span></td>
                <td style={td}>{fmtPct(g.comp_pct)}</td>
                <td style={td}>{fmtWon(g.box_limit_krw)}</td>
                <td style={td}>{g.deadline_basis && g.deadline_days != null ? `${labelOf('deadline_basis', g.deadline_basis, labels)}+${g.deadline_days}일` : '—'}</td>
                <td style={td}>{g.loss_basis ? `${labelOf('deadline_basis', g.loss_basis, labels)} ${g.loss_start_days ?? '—'}~${g.loss_end_days ?? '—'}일` : '—'}</td>
                <td style={{ ...td, color: COLORS.textSub }}>{g.match_keywords || '—'}</td>
                <td style={td}>{g.not_applicable ? '해당없음' : '—'}</td>
                <td style={td}>
                  <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
                    {g.needs_check && <NeedsCheck />}
                    {!g.active && <Inactive />}
                  </span>
                </td>
                {canEdit && (
                  <td style={td}>
                    <Button variant="quiet" size="sm" aria-label={`${g.name} 수정`} onClick={() => openEditor(g.code)}>수정</Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && editing === 'new' && <GroupEditor key="new" group={null} labels={labels} runSave={runSave} onClose={() => setEditing(null)} />}
      {canEdit && current && <GroupEditor key={current.code} group={current} labels={labels} runSave={runSave} onClose={() => setEditing(null)} />}

      <h3 style={{ margin: '18px 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.text }}>WMS 택배사 코드 매핑</h3>
      {unmapped > 0 && (
        <div data-testid="claim-unmapped-warning" style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: COLORS.dangerText }}>
          미매핑 {unmapped}건 — 확정 그룹이 없거나 확정하지 않은 코드가 있습니다.
        </div>
      )}
      {codeError && <div role="alert" style={{ ...errText, marginTop: 0, marginBottom: '8px' }}>{codeError}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
          <thead>
            <tr>
              <th style={th}>출처</th><th style={th}>코드</th><th style={th}>이름</th><th style={th}>추정 그룹</th>
              <th style={th}>확정 그룹</th><th style={th}>상태</th>{canEdit && <th style={th}>동작</th>}
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} style={{ ...td, color: COLORS.textMuted }}>아직 동기화한 택배사 코드가 없습니다.</td></tr>
            )}
            {codes.map((c) => {
              const pick = pickOf(c);
              const settled = c.confirmed && pick === c.group_code;
              return (
                <tr key={keyOf(c)} data-testid="claim-code-row">
                  <td style={td}>{SOURCE_LABELS[c.source] || c.source}</td>
                  <td style={td}>{c.code}</td>
                  <td style={td}>{c.name || '—'}</td>
                  <td style={{ ...td, color: COLORS.textSub }}>{c.suggested_group ? groupName(c.suggested_group) : '—'}</td>
                  <td style={td}>
                    <select aria-label={`${SOURCE_LABELS[c.source] || c.source} ${c.code} 확정 그룹`} value={pick} disabled={!canEdit}
                      onChange={(e) => { const v = e.target.value; setPicks((p) => ({ ...p, [keyOf(c)]: v })); }}
                      style={{ ...input, width: '160px' }}>
                      <option value="">—(미정)</option>
                      {activeGroups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                      {pick && !activeGroups.some((g) => g.code === pick) && <option value={pick}>{groupName(pick)}</option>}
                    </select>
                  </td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span style={badge(c.confirmed ? 'success' : 'gray')}>{c.confirmed ? '확정' : '미확정'}</span>
                      {!c.in_source && <span style={badge('danger')}>WMS 에 없음</span>}
                    </span>
                  </td>
                  {canEdit && (
                    <td style={td}>
                      <Button variant="secondary" size="sm" aria-label={`${c.code} 확정`} disabled={settled} onClick={() => onConfirm(c)}>확정</Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ── 고객사 배너 ─────────────────────────────────────────────────────────────
// 미등록 → [이 고객사 기준 등록] · 등록·예외 없음 → '공통 기준 적용 중' + [고객사 예외 기준 만들기]
// · 예외 있음 → '고객사 예외 기준' 배지 + [기준 정보 수정]. 단추는 canEdit 일 때만.
function ShipperStandardEditor({ standard, runSave, onClose }) {
  const [draft, setDraft] = useState(() => ({
    temp_zone: standard.temp_zone || '', doc_ref: asText(standard.doc_ref), note: asText(standard.note), reason: '',
  }));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const onSave = async () => {
    const body = changedFields(
      { temp_zone: standard.temp_zone, doc_ref: standard.doc_ref ?? null, note: standard.note ?? null },
      { temp_zone: draft.temp_zone, doc_ref: textOrNull(draft.doc_ref), note: textOrNull(draft.note) },
    );
    if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
    setBusy(true);
    setError('');
    const msg = await runSave(() => claimsAPI.patchShipperStandard(standard.shipper_id, withReason(body, draft.reason)));
    setBusy(false);
    if (msg) setError(msg); else onClose();
  };

  return (
    <div data-testid="claim-shipper-editor" style={editorBox}>
      <div style={formGrid}>
        <div role="radiogroup" aria-label="고객사 품온" style={{ display: 'flex', gap: '12px', paddingBottom: '7px' }}>
          {SHIPPER_ZONES.map((z) => (
            <label key={z.key} style={checkBox}>
              <input type="radio" name="claim-shipper-edit-zone" value={z.key} checked={draft.temp_zone === z.key} onChange={() => set('temp_zone')(z.key)} />
              {z.label}
            </label>
          ))}
        </div>
        <Field label="기준 공문 번호">
          <input type="text" value={draft.doc_ref} maxLength={100} onChange={(e) => set('doc_ref')(e.target.value)} style={{ ...input, width: '200px' }} />
        </Field>
        <Field label="비고">
          <input type="text" value={draft.note} maxLength={300} onChange={(e) => set('note')(e.target.value)} style={{ ...input, width: '240px' }} />
        </Field>
        <ReasonInput value={draft.reason} onChange={set('reason')} />
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ ...rowActions, marginTop: '10px' }}>
        <Button onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>
        <Button variant="secondary" onClick={onClose}>닫기</Button>
      </div>
    </div>
  );
}

function ShipperBanner({ scope, canEdit, runSave, onRegistered, guard }) {
  const [mode, setMode] = useState(null);   // null | 'register' | 'edit'
  const [busy, setBusy] = useState(false);
  const standard = scope.standard || null;
  const fixed = {
    id: scope.shipper_id, display_name: scope.display_name, center_name: scope.center_name, temp_zone_hint: scope.temp_zone_hint,
  };
  const toggle = (next) => setMode((m) => (m === next ? null : next));

  const onOverrides = async () => {
    if (!(await guard())) return;
    setBusy(true);
    await runSave(() => claimsAPI.createShipperOverrides(scope.shipper_id, {}), '고객사 예외 기준을 만들었습니다');
    setBusy(false);
  };

  let state;
  let text;
  if (!scope.registered) {
    state = <span style={badge('danger')}>기준 미등록</span>;
    text = '이 고객사의 기준이 아직 없습니다 — 아래는 공통 기준입니다.';
  } else if (!scope.has_overrides) {
    state = <span style={badge('gray')}>공통 기준 적용 중</span>;
    text = '고객사 예외 기준이 없어 공통 기준을 그대로 씁니다.';
  } else {
    state = <span style={badge('blue')}>고객사 예외 기준</span>;
    text = '이 고객사에만 적용하는 기준입니다(공통 기준과 따로 저장).';
  }

  return (
    <div role="region" aria-label="고객사 기준" data-testid="claim-shipper-banner"
      style={{ ...card, marginTop: '12px', borderColor: scope.registered ? COLORS.primaryBorder : COLORS.danger }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {state}
        {standard && !standard.active && <Inactive title="고객사 기준" />}
        {standard && standard.needs_check && <NeedsCheck title="고객사 기준" />}
        <span style={{ fontSize: '13px', color: COLORS.textSub }}>{text}</span>
        {canEdit && (
          <span style={{ marginLeft: 'auto', ...rowActions }}>
            {!scope.registered && (
              <Button size="sm" aria-pressed={mode === 'register'} onClick={() => toggle('register')}>이 고객사 기준 등록</Button>
            )}
            {scope.registered && !scope.has_overrides && (
              <Button size="sm" onClick={onOverrides} disabled={busy}>{busy ? '만드는 중…' : '고객사 예외 기준 만들기'}</Button>
            )}
            {scope.registered && (
              <Button variant="secondary" size="sm" aria-pressed={mode === 'edit'} onClick={() => toggle('edit')}>기준 정보 수정</Button>
            )}
          </span>
        )}
      </div>
      {canEdit && mode === 'register' && !scope.registered && (
        <div style={{ marginTop: '10px', maxWidth: '360px' }}>
          <ShipperStandardForm fixed={fixed} onCancel={() => setMode(null)}
            onDone={async () => { setMode(null); await onRegistered(); }} />
        </div>
      )}
      {canEdit && mode === 'edit' && standard && (
        <ShipperStandardEditor key={standard.updated_at || standard.id} standard={standard} runSave={runSave} onClose={() => setMode(null)} />
      )}
    </div>
  );
}

// ── 엑셀 업로드 패널 ────────────────────────────────────────────────────────
function UploadPanel({ onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');   // '' | 'download' | 'preview' | 'apply'
  const [error, setError] = useState('');
  const options = (dryRun) => {
    const opts = { dryRun };
    const r = textOrNull(reason);
    if (r) opts.reason = r;
    return opts;
  };
  const fail = (e, fallback) => {
    const msg = errorText(e, fallback);
    setError(msg);
    toast(msg, 'error');
  };

  const onDownload = async () => {
    setBusy('download');
    setError('');
    try {
      const r = await claimsAPI.exportCostItems();
      blobDownload(r.data, fileNameOf(r, EXPORT_FALLBACK));
    } catch (e) {
      fail(e, '단가표를 내려받지 못했습니다');
    } finally {
      setBusy('');
    }
  };
  const onPreview = async () => {
    if (!file) { setError('올릴 단가표 파일(xlsx)을 고르세요.'); return; }
    setBusy('preview');
    setError('');
    setPreview(null);
    try {
      const r = await claimsAPI.importCostItems(file, options(true));
      setPreview(r.data || { changes: [], unchanged: 0 });
    } catch (e) {
      fail(e, '미리보기에 실패했습니다');
    } finally {
      setBusy('');
    }
  };
  const onApply = async () => {
    setBusy('apply');
    setError('');
    try {
      await claimsAPI.importCostItems(file, options(false));
    } catch (e) {
      fail(e, '반영에 실패했습니다');
      setBusy('');
      return;
    }
    setBusy('');
    toast('단가표를 반영했습니다');
    await onApplied();
  };

  const changes = preview && Array.isArray(preview.changes) ? preview.changes : [];
  return (
    <div role="dialog" aria-label="엑셀 업로드" data-testid="claim-upload-panel" style={{ ...card, marginTop: '12px' }}>
      <div style={sectionHead}>
        <h2 style={sectionTitle}>엑셀 업로드 — 비용 단가표 일괄 반영</h2>
        <div style={{ marginLeft: 'auto' }}><Button variant="secondary" size="sm" onClick={onClose}>닫기</Button></div>
      </div>
      <p style={guide}>현재 단가표를 내려받아 항목·기준·단가·최대 단가·비고를 고친 뒤 올립니다. 코드로 행을 찾고, 한 행이라도 틀리면 아무것도 바꾸지 않습니다.</p>
      <div style={formGrid}>
        <Button variant="secondary" size="sm" onClick={onDownload} disabled={busy === 'download'}>{busy === 'download' ? '내려받는 중…' : '현재 단가표 내려받기'}</Button>
        <Field label="단가표 파일(xlsx)">
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setFile((e.target.files && e.target.files[0]) || null); setPreview(null); }} />
        </Field>
        <ReasonInput value={reason} onChange={setReason} />
        <Button variant="secondary" onClick={onPreview} disabled={!file || busy === 'preview'}>{busy === 'preview' ? '확인 중…' : '미리보기'}</Button>
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
      {preview && (
        <div data-testid="claim-upload-preview" style={{ marginTop: '12px' }}>
          <p style={guide}>바뀌는 행 {changes.length}개 · 그대로 {preview.unchanged ?? 0}개</p>
          {changes.length === 0 ? (
            <p style={{ ...guide, color: COLORS.textMuted }}>바뀔 칸이 없습니다.</p>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>코드</th><th style={th}>이름</th><th style={th}>바뀌는 칸</th></tr></thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.code} data-testid="claim-upload-change">
                      <td style={td}>{c.code}</td>
                      <td style={td}>{c.name}</td>
                      <td style={td}>
                        {Object.keys(c.after || {}).map((k) => `${FIELD_LABELS[k] || k}: ${historyValue((c.before || {})[k])} → ${historyValue(c.after[k])}`).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ ...rowActions, marginTop: '10px' }}>
                <Button onClick={onApply} disabled={busy === 'apply'}>{busy === 'apply' ? '반영 중…' : '반영'}</Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── 화면 ──────────────────────────────────────────────────────────────────
export default function ClaimStandardsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');   // loading | ready | forbidden | error
  const [loadError, setLoadError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [zone, setZone] = useState('ambient');
  const [compDraft, setCompDraft] = useState({});      // 규칙 id → {칸: 입력 글자}
  const [costDraft, setCostDraft] = useState({});      // 단가 id → {칸: 입력 글자}
  const [settingDraft, setSettingDraft] = useState({}); // 설정 key → 입력 글자
  const [shipperDraft, setShipperDraft] = useState({}); // 고객사 제품가 기준 칸 → 입력 글자(기준 '' = 공통 따름)
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState('');
  const [errorKey, setErrorKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shipperId, setShipperId] = useState(null);   // null = 공통 기준 · 숫자 = 고른 고객사(center_shippers.id)
  const [panelKey, setPanelKey] = useState(0);         // 고객사 목록을 다시 읽게 한다(배너에서 등록·예외 만들기 뒤)
  const loadSeq = useRef(0);                           // 고객사를 빨리 바꿔도 마지막 요청의 응답만 쓴다

  const load = useCallback(async () => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    try {
      const r = await (shipperId === null ? claimsAPI.standards() : claimsAPI.standards({ shipper_id: shipperId }));
      if (seq !== loadSeq.current) return;
      setData(r.data || {});
      setStatus('ready');
    } catch (e) {
      if (seq !== loadSeq.current) return;
      if (statusOf(e) === 403) { setStatus('forbidden'); return; }
      const msg = errorText(e, '보상기준을 불러오지 못했습니다');
      if (statusOf(e) === 404 && shipperId !== null) {   // 고객사가 사라졌다 — 공통 기준으로 돌아간다
        toast(msg, 'error');
        setShipperId(null);
        setPanelKey((k) => k + 1);
        return;
      }
      setLoadError(msg);
      setStatus((s) => (s === 'ready' ? s : 'error'));
      toast(msg, 'error');
    }
  }, [shipperId]);

  useEffect(() => { load(); }, [load]);

  // 편집기 저장 1회 — 성공하면 GET 을 다시 읽고 이력 패널을 새로 그린다 · 실패면 문구를 돌려준다(부르는 쪽이 칸 옆에 보인다).
  const runSave = useCallback(async (call, okText = '저장했습니다') => {
    try {
      await call();
    } catch (e) {
      const msg = errorText(e);
      toast(msg, 'error');
      return msg;
    }
    toast(okText);
    await load();
    setHistoryKey((k) => k + 1);
    return '';
  }, [load]);

  const afterSync = useCallback(async () => {
    await load();
    setHistoryKey((k) => k + 1);
  }, [load]);
  // 배너의 쓰기(예외 기준 만들기·기준 정보 수정) — runSave 와 같고, 성공하면 고객사 목록도 다시 읽는다.
  const runShipperSave = useCallback(async (call, okText) => {
    const msg = await runSave(call, okText);
    if (!msg) setPanelKey((k) => k + 1);
    return msg;
  }, [runSave]);
  const afterRegistered = useCallback(async () => {
    setPanelKey((k) => k + 1);
    await afterSync();
  }, [afterSync]);

  const canEdit = !!(data && data.can_edit);
  const labels = (data && data.rule_labels) || null;
  const codes = data && data.courier_codes ? data.courier_codes : { items: [], unmapped_count: 0 };
  const scope = data && data.scope ? data.scope : null;
  const scopeKey = scope && scope.has_overrides ? (scope.shipper_key || '') : '';
  const inScope = useMemo(() => inScopeOf(scopeKey), [scopeKey]);
  const rules = useMemo(() => (data && Array.isArray(data.comp_rules) ? data.comp_rules : []), [data]);
  const costs = useMemo(() => sortRows((data && Array.isArray(data.cost_items) ? data.cost_items : []).filter(inScope)), [data, inScope]);
  const settings = useMemo(() => (data && Array.isArray(data.settings) ? data.settings : []), [data]);
  const groups = useMemo(() => (data && Array.isArray(data.courier_groups) ? data.courier_groups : []), [data]);
  const byKey = useMemo(() => Object.fromEntries(settings.map((s) => [s.key, s])), [settings]);
  const basisSetting = byKey.product_price_basis || null;
  const ratioSetting = byKey.product_sale_ratio_pct || null;
  const rulesSetting = byKey[RULE_TEXTS_KEY] || null;
  const opsSettings = settings.filter((s) => s.key !== RULE_TEXTS_KEY && !HIDDEN_SETTING_KEYS.includes(s.key)
    && !(basisSetting && PRICE_KEYS.includes(s.key)));
  // 고객사 제품가 기준을 정할 수 있는 범위 — 등록된 고객사(scope.standard). 미등록·공통 범위는 null.
  const shipperStd = scope && scope.registered && scope.standard ? scope.standard : null;

  const pendingCount = countDraft(compDraft) + countDraft(costDraft) + Object.keys(settingDraft).length
    + Object.keys(shipperDraft).length;
  const settingValue = useCallback((key) => (has(settingDraft, key) ? settingDraft[key] : asText(byKey[key] && byKey[key].value)), [settingDraft, byKey]);

  const onComp = useCallback((r, key, raw) => {
    setCompDraft((prev) => putDraft(prev, r.id, key, raw, COMP_CONVERT[key](raw) === compOriginal(r, key)));
  }, []);
  const onCost = useCallback((it, key, raw) => {
    setCostDraft((prev) => putDraft(prev, it.id, key, raw, numOrNull(raw) === (it[key] ?? null)));
  }, []);
  const onSetting = useCallback((s, raw) => {
    setSettingDraft((prev) => {
      const next = { ...prev };
      if (raw === asText(s.value)) delete next[s.key]; else next[s.key] = raw;
      return next;
    });
  }, []);
  // 고객사 제품가 기준 — {칸: 입력 글자} 를 한 번에(공통 따름은 기준·비율을 함께 비운다) · 원래 값과 같으면 초안에서 지운다.
  const onShipper = useCallback((patch) => {
    const orig = shipperPriceOrig(shipperStd);
    setShipperDraft((prev) => {
      const next = { ...prev };
      Object.entries(patch).forEach(([key, raw]) => { if (raw === orig[key]) delete next[key]; else next[key] = raw; });
      return next;
    });
  }, [shipperStd]);
  const clearDrafts = useCallback(() => {
    setCompDraft({});
    setCostDraft({});
    setSettingDraft({});
    setShipperDraft({});
    setReason('');
    setSaveError('');
    setErrorKey('');
  }, []);
  // 초안이 있는 채로 편집기·추가·업로드·동기화 — 물어보고, 계속하면 초안을 버린다.
  const guard = useCallback(async () => {
    if (!pendingCount) return true;
    const ok = await confirmDialog(`저장하지 않은 변경 ${pendingCount}건이 있습니다. 계속하면 이 변경은 버립니다. 계속할까요?`);
    if (!ok) return false;
    clearDrafts();
    return true;
  }, [pendingCount, clearDrafts]);

  // 고객사 고르기(null = 공통 기준) — 초안이 있으면 먼저 묻는다 · 바뀌면 load 가 그 범위로 다시 읽는다.
  const onSelectShipper = useCallback(async (id) => {
    const next = id === undefined ? null : id;
    if (next === shipperId) return;
    if (!(await guard())) return;
    setShipperId(next);
  }, [shipperId, guard]);

  // 고객사 품온 — 고객사 기준의 품온(없으면 명부 제안)으로 품온 토글을 맞춘다(고객사가 바뀔 때만).
  const scopeShipperId = scope ? scope.shipper_id : null;
  const scopeZone = scope ? ((scope.standard && scope.standard.temp_zone) || scope.temp_zone_hint || null) : null;
  useEffect(() => {
    if (scopeZone) setZone(scopeZone);
  }, [scopeShipperId, scopeZone]);

  const onSave = async () => {
    if (!pendingCount || saving) return;
    // 공통 제품가 기준은 공통 범위에서만 고친다(고객사 범위에는 공통 토글이 없다).
    if (!scope && basisSetting && ratioSetting && settingValue(basisSetting.key) === 'sale' && !settingValue(ratioSetting.key).trim()) {
      setSaveError(SALE_RATIO_MISSING);
      setErrorKey(`settings:${ratioSetting.key}`);
      return;
    }
    const shipperKeys = shipperStd ? Object.keys(shipperDraft) : [];
    if (shipperKeys.length) {
      const orig = shipperPriceOrig(shipperStd);
      const value = (key) => (has(shipperDraft, key) ? shipperDraft[key] : orig[key]);
      if (value('product_price_basis') === 'sale' && !String(value('product_sale_ratio_pct')).trim()) {
        setSaveError(SALE_RATIO_MISSING);
        setErrorKey('shipper_standard');
        return;
      }
    }
    const body = {};
    const compRows = Object.entries(compDraft).map(([id, row]) => ({ id: Number(id), ...mapRow(row, (k, v) => COMP_CONVERT[k](v)) }));
    const costRows = Object.entries(costDraft).map(([id, row]) => ({ id: Number(id), ...mapRow(row, (k, v) => numOrNull(v)) }));
    if (compRows.length) body.comp_rules = compRows;
    if (costRows.length) body.cost_items = costRows;
    if (Object.keys(settingDraft).length) body.settings = { ...settingDraft };
    // 고객사 제품가 기준 — 바뀐 칸만 · 기준 '' = null(공통 따름) · 비율은 수(빈칸 = null).
    if (shipperKeys.length) {
      body.shipper_standard = {
        shipper_id: shipperStd.shipper_id,
        ...mapRow(shipperDraft, (k, v) => (k === 'product_price_basis' ? (v || null) : numOrNull(v))),
      };
    }
    const r = textOrNull(reason);
    if (r) body.reason = r;
    setSaving(true);
    setSaveError('');
    setErrorKey('');
    try {
      await claimsAPI.saveStandards(body);
    } catch (e) {
      const msg = errorText(e);
      setSaveError(msg);
      setErrorKey(errorTarget(fieldOf(e)));
      toast(msg, 'error');
      setSaving(false);
      return;
    }
    setSaving(false);
    clearDrafts();
    toast('저장했습니다');
    await load();
    setHistoryKey((k) => k + 1);
  };

  const onUpload = async () => {
    if (uploadOpen) { setUploadOpen(false); return; }
    if (await guard()) setUploadOpen(true);
  };
  const afterUpload = useCallback(async () => {
    setUploadOpen(false);
    await load();
    setHistoryKey((k) => k + 1);
  }, [load]);

  // 머리 부제 — 공통: 기준 공문 번호 · 공통 행의 마지막 수정일(KST).
  //   고객사: '{고객사} · {센터} / 공문 {고객사 기준 공문 번호} 기준 · {보이는 행·고객사 기준의 마지막 수정일} 수정'.
  const scopeStandard = scope && scope.standard ? scope.standard : null;
  const lastUpdated = useMemo(() => {
    const rows = scope
      ? [...rules.filter(inScope), ...costs, ...(scopeStandard ? [scopeStandard] : [])]
      : [...rules.filter(inScope), ...costs, ...settings, ...groups];
    const stamps = rows.map((row) => row.updated_at).filter(Boolean);
    return stamps.length ? stamps.reduce((a, b) => (a > b ? a : b)) : '';
  }, [scope, scopeStandard, rules, inScope, costs, settings, groups]);
  const day = lastUpdated ? fmtKstDateTime(lastUpdated).slice(0, 10) : '';
  const docRef = asText(byKey.standard_doc_ref && byKey.standard_doc_ref.value).trim();
  const shipperDoc = asText(scopeStandard && scopeStandard.doc_ref).trim();
  let subtitle = '공통 기준';
  if (status === 'ready' && scope) {
    const where = [scope.display_name, scope.center_name].filter(Boolean).join(' · ');
    subtitle = `${where} / 공문 ${shipperDoc || '미입력'} 기준${day ? ` · ${day} 수정` : ''}`;
  } else if (status === 'ready') {
    subtitle = `공통 기준 · 기준 공문 ${docRef || '미입력'}${day ? ` · ${day} 수정` : ''}`;
  }

  return (
    <AppShell app="claim-standards">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', display: 'flex', overflow: 'hidden', background: `var(--gw-bg, ${COLORS.bg})` }}>
        {status === 'ready' && (
          <ClaimShipperPanel canEdit={canEdit} selectedId={shipperId} onSelect={onSelectShipper} reloadKey={panelKey} onChanged={afterSync} />
        )}
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <Page>
            <PageHeader icon={IconReportMoney} tone="red" title="보상기준 마스터"
              subtitle={subtitle}
              actions={(
                <>
                  {status === 'ready' && !canEdit && <span data-testid="claim-readonly" style={badge('gray')}>보기 전용</span>}
                  {status === 'ready' && canEdit && !scopeKey && (
                    <Button variant="secondary" aria-pressed={uploadOpen} onClick={onUpload}>엑셀 업로드</Button>
                  )}
                  <Button variant="secondary" aria-pressed={historyOpen} onClick={() => setHistoryOpen((v) => !v)}>변경이력</Button>
                  {status === 'ready' && canEdit && (
                    <Button data-testid="claim-save" onClick={onSave} disabled={!pendingCount || saving}>{saving ? '저장 중…' : '저장'}</Button>
                  )}
                </>
              )} />
            {status === 'loading' && <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</div>}
            {status === 'forbidden' && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>이 화면을 볼 권한이 없습니다.</div>}
            {status === 'error' && (
              <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                {loadError}
                <Button variant="secondary" size="sm" onClick={() => load()}>다시 시도</Button>
              </div>
            )}
            {status === 'ready' && data && (
              <>
                {canEdit && uploadOpen && !scopeKey && <UploadPanel onClose={() => setUploadOpen(false)} onApplied={afterUpload} />}
                {scope && (
                  <ShipperBanner key={scope.shipper_id} scope={scope} canEdit={canEdit} runSave={runShipperSave}
                    onRegistered={afterRegistered} guard={guard} />
                )}
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <ZoneToggle zone={zone} onChange={setZone} />
                  <span style={{ ...guide, margin: 0 }}>품온마다 변상 기준이 따로 있습니다.</span>
                </div>
                {canEdit && (pendingCount > 0 || saveError) && (
                  <div role="region" aria-label="저장 대기" data-testid="claim-pending"
                    style={{ ...card, marginTop: '12px', position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', borderColor: COLORS.primaryBorder, background: COLORS.primarySoft }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: COLORS.primaryDark, paddingBottom: '7px' }}>변경 {pendingCount}건</span>
                    <ReasonInput value={reason} onChange={setReason} />
                    <Button variant="secondary" size="sm" onClick={clearDrafts} style={{ marginBottom: '2px' }}>되돌리기</Button>
                    {saveError && <div role="alert" style={{ ...errText, marginTop: 0, flexBasis: '100%' }}>{saveError}</div>}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'flex-start' }}>
                  <div style={{ flex: '3 1 560px', minWidth: 0 }}>
                    <CompSection rules={rules} zone={zone} scopeKey={scopeKey} labels={labels} canEdit={canEdit} runSave={runSave}
                      draft={compDraft} onDraft={onComp} errorKey={errorKey} guard={guard} />
                  </div>
                  <div style={{ flex: '2 1 360px', minWidth: 0 }}>
                    <CostSection items={costs} canEdit={canEdit} canAdd={!scopeKey} runSave={runSave} draft={costDraft} onDraft={onCost} errorKey={errorKey} guard={guard}
                      basisSetting={basisSetting} ratioSetting={ratioSetting} valueOf={settingValue} onSetting={onSetting}
                      scope={scope} shipperDraft={shipperDraft} onShipper={onShipper} />
                  </div>
                </div>
                <RulesSection setting={rulesSetting} settings={opsSettings} canEdit={canEdit} valueOf={settingValue} onSetting={onSetting} errorKey={errorKey} />
                <CourierSection groups={groups} codes={codes.items || []} unmapped={codes.unmapped_count || 0}
                  labels={labels} canEdit={canEdit} runSave={runSave} onSynced={afterSync} guard={guard} />
              </>
            )}
          </Page>
        </div>
        {historyOpen && (
          <ClaimHistoryPanel key={historyKey} load={claimsAPI.standardsHistory} onClose={() => setHistoryOpen(false)} />
        )}
      </main>
    </AppShell>
  );
}
